import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  NgCommonModule,
  TypedFormControl,
  TypedFormGroup,
  validFormGroupChanges$,
} from '@principle-theorem/ng-shared';
import {
  ICustomReportFilter,
  ReportFilterComparison,
} from '@principle-theorem/principle-core/interfaces';
import {
  CustomReportFilter,
  ReportBuilderDataSourceInstance,
} from '@principle-theorem/reporting';
import { ICustomReportFilterFormatter } from '@principle-theorem/reporting/interfaces';
import { debounceUserInput, toInt } from '@principle-theorem/shared';
import { first, isNil, isNumber } from 'lodash';
import { map } from 'rxjs/operators';

interface INumberFilterForm {
  comparison: ReportFilterComparison;
  value: number;
}

@Component({
  selector: 'pr-edit-report-filter-number',
  templateUrl: './edit-report-filter-number.component.html',
  styleUrls: ['./edit-report-filter-number.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInput,
    NgCommonModule,
  ],
})
export class EditReportFilterNumberComponent {
  formGroup = new TypedFormGroup<INumberFilterForm>({
    comparison: new TypedFormControl<ReportFilterComparison>(
      ReportFilterComparison.IsEqualTo,
      Validators.required
    ),
    value: new TypedFormControl<number>(undefined, Validators.required),
  });
  comparisons = CustomReportFilter.getComparisonOptions([
    ReportFilterComparison.IsEqualTo,
    ReportFilterComparison.GreaterThan,
    ReportFilterComparison.GreaterThanOrEqualTo,
    ReportFilterComparison.LessThan,
    ReportFilterComparison.LessThanOrEqualTo,
  ]);

  dataSource = input.required<ReportBuilderDataSourceInstance>();
  filter = input.required<ICustomReportFilter>();
  filterChange = output<Partial<ICustomReportFilter>>();

  formatter: Signal<ICustomReportFilterFormatter>;

  constructor() {
    this.formatter = computed(() =>
      CustomReportFilter.getFormatter(
        this.dataSource().factTable,
        this.filter()
      )
    );

    effect(() => {
      const formData = this._toFormData(this.filter());
      this.formGroup.patchValue(formData, { emitEvent: false });
    });

    validFormGroupChanges$(this.formGroup)
      .pipe(
        debounceUserInput(),
        map((value) => this._fromFormData(value)),
        takeUntilDestroyed()
      )
      .subscribe((filterChanges) => this.filterChange.emit(filterChanges));
  }

  private _toFormData(filter: ICustomReportFilter): Partial<INumberFilterForm> {
    const values = filter.values.map((value) =>
      isNil(value)
        ? undefined
        : isNumber(value.value)
          ? value.value
          : toInt(value.value)
    );

    const comparison = this._getValidComparison(filter);
    return { comparison, value: first(values) };
  }

  private _getValidComparison(
    filter: ICustomReportFilter
  ): ReportFilterComparison {
    const validComparisons = this.comparisons.map(
      (comparison) => comparison.value
    );
    const comparison = CustomReportFilter.getComparison(filter);
    if (comparison && validComparisons.includes(comparison)) {
      return comparison;
    }
    return first(validComparisons) ?? ReportFilterComparison.IsEqualTo;
  }

  private _fromFormData(
    formData: INumberFilterForm
  ): Partial<ICustomReportFilter> {
    return {
      comparison: formData.comparison,
      values: [{ label: `${formData.value}`, value: formData.value }],
      dateType: undefined,
      relativeOffset: undefined,
    };
  }
}
